import { cva } from 'class-variance-authority';
import type * as React from 'react';
import type { ReactNode } from 'react';

import { cn } from '../../styles/lib/utils';
import { Tooltip } from '../Tooltip';

export const PARTITION_BAR_TONES = ['green', 'blue', 'yellow', 'grey'] as const;
export type PartitionBarTone = (typeof PARTITION_BAR_TONES)[number];

const partitionToneVariants = cva('', {
  variants: {
    tone: {
      green: 'bg-green-50',
      blue: 'bg-blue-50',
      yellow: 'bg-yellow-60',
      grey: 'bg-grey-30',
    },
  },
  defaultVariants: { tone: 'grey' },
});

export type PartitionBarItem = {
  label: string;
  value: number;
  tone: PartitionBarTone;
  /** Text shown for the value, e.g. "45%". Defaults to `value`. */
  displayValue?: ReactNode;
  /** Shown in the segment and legend tooltip. */
  description?: ReactNode;
  /** Keeps the segment in the bar but leaves its legend entry to screen readers only. */
  hideFromLegend?: boolean;
};

export type PartitionBarProps = React.ComponentProps<'div'> & {
  items: PartitionBarItem[];
  'data-testid'?: string;
};

function toSlug(label: string) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function PartitionTooltipContent({ item }: { item: PartitionBarItem }) {
  return (
    <div className="max-w-xs space-y-1 py-1 text-white">
      <p className="text-sm font-medium">
        {item.label} - <span className="font-bold">{item.displayValue ?? item.value}</span>
      </p>
      {item.description ? <p className="text-xs">{item.description}</p> : null}
    </div>
  );
}

/**
 * A stacked proportional bar with a legend. Each legend entry carries its value as text, so the
 * segments are never told apart by color or width alone; the bar itself is hidden from screen readers.
 */
function PartitionBar({ items, className, 'data-testid': dataTestId, ...props }: PartitionBarProps) {
  const visibleItems = items.filter((item) => item.value > 0);

  return (
    <div data-slot="partition-bar" data-testid={dataTestId} className={cn('space-y-2', className)} {...props}>
      <div
        aria-hidden="true"
        data-slot="partition-bar-track"
        className="flex h-6 w-full gap-0.5 overflow-hidden rounded-full border border-grey-30 bg-white"
      >
        {visibleItems.map((item) => {
          const segment = (
            <div
              key={item.label}
              data-slot="partition-bar-segment"
              className={cn('h-full', partitionToneVariants({ tone: item.tone }))}
              style={{ flex: `${item.value} 1 0%` }}
            />
          );
          return item.description ? (
            <Tooltip key={item.label} asChild contentChildren={<PartitionTooltipContent item={item} />}>
              {segment}
            </Tooltip>
          ) : (
            segment
          );
        })}
      </div>

      <ul data-slot="partition-bar-legend" className="flex flex-wrap justify-between gap-2 px-1">
        {visibleItems.map((item) => {
          const text = (
            <span>
              {item.label} <span className="font-medium">{item.displayValue ?? item.value}</span>
            </span>
          );
          return (
            <li
              key={item.label}
              data-slot="partition-bar-legend-item"
              className={cn('flex items-center gap-2 text-sm text-grey-90', item.hideFromLegend && 'sr-only')}
            >
              <span
                aria-hidden="true"
                className={cn('size-4 shrink-0 rounded-full', partitionToneVariants({ tone: item.tone }))}
              />
              {item.description && !item.hideFromLegend ? (
                <Tooltip
                  contentChildren={<PartitionTooltipContent item={item} />}
                  data-testid={dataTestId ? `${dataTestId}-legend-${toSlug(item.label)}` : undefined}
                >
                  {text}
                </Tooltip>
              ) : (
                text
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export { PartitionBar };
